<template>
  <v-dialog v-model="dialog" width="80%">
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" dark v-bind="attrs" v-on="on">
        Visualize this scenario
      </v-btn>
    </template>
    <v-card>
      <v-card-title class="cardheader">
        <v-row>
          <span class="headline black--text">
            Visualization of evasive maneuver</span
          >
          <v-spacer />
          <v-btn icon light small class="m-0" @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <EvasiveManeuverCesiumVisualizer
              v-if="propagationDates.length > 0"
              :nominalTargetPositions="
                displayNominalTarget ? nominalTargetPositions : null
              "
              :maneuveringTargetPositions="maneuveringTargetPositions"
              :chaserPositions="chaserPositions"
              :dates="propagationDates"
              :id="id"
              :key="id"
              :targetManeuverDates="targetManeuverDates"
              :chaserManeuverDates="chaserManeuverDates"
              :satelliteWithSphereIds="satelliteWithSphereIds"
              v-on:updateSatelliteIds="updateSatelliteSelector($event)"
          /></v-row>
          <v-row>
            <v-col>
              <v-row>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on">
                      <v-checkbox
                        v-model="displayChaserReaction"
                        :disabled="!availableChaserReaction"
                        :label="`Display Chaser Reaction Maneuvers`"
                      ></v-checkbox>
                    </div>
                  </template>
                  <span v-if="availableChaserReaction"
                    >Change the trajectory of the chaser to include a reaction
                    maneuver</span
                  >
                  <span v-else
                    >The chaser reaction maneuver is not available for this
                    maneuver.
                  </span>
                </v-tooltip>
              </v-row>
            </v-col>
            <v-col>
              <v-row>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on">
                      <v-checkbox
                        v-model="displayNominalTarget"
                        :label="`Display Nominal Trajectory`"
                      ></v-checkbox>
                    </div>
                  </template>
                  <span
                    >Display the trajectory the target would follow without
                    maneuver</span
                  >
                </v-tooltip>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-select
              label="Display minimum approach distance threshold around "
              :items="satelliteIds"
              v-model="satelliteWithSphereIds"
              multiple
          /></v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from '../../services/axios'
import Vue from 'vue'
import VueAxios from 'vue-axios'
import errorMixin from '../../mixins/logHandler.js'
import { mapGetters } from 'vuex'
Vue.use(VueAxios, axios)
import EvasiveManeuverCesiumVisualizer from './EvasiveManeuverCesiumVisualizer'

export default {
  name: 'EvasiveManeuverCesiumDialog',

  mixins: [errorMixin],

  props: {
    maneuverReport: {
      type: Object
    },
    id: {
      type: String
    }
  },

  components: { EvasiveManeuverCesiumVisualizer },

  created() {},

  mounted() {},

  watch: {},

  data() {
    return {
      dialog: false,
      displayNominalTarget: true,
      displayChaserReaction: false,
      satelliteWithSphereIds: [],
      satelliteIds: []
    }
  },

  computed: {
    ...mapGetters({
      currentMission: 'currentMission'
    }),
    computeChaserReaction() {
      return this.currentMission?.computeChaserReaction
    },
    nominalTargetPositions() {
      const positions =
        this.maneuverReport?.evasiveTargetToNominalRelativeDistanceResults
          ?.referencePositions
      return positions ? positions : []
    },

    maneuveringTargetPositions() {
      const positions =
        this.maneuverReport?.targetReactionManeuverReport
          ?.relativeDistanceResults?.referencePositions
      return positions ? positions : []
    },

    availableChaserReaction() {
      return (
        this.maneuverReport?.chaserReactionManeuverReport
          ?.relativeDistanceResults?.otherObjectPositions != null
      )
    },

    chaserPositions() {
      const maneuveringChaserPositions =
        this.maneuverReport?.chaserReactionManeuverReport
          ?.relativeDistanceResults?.otherObjectPositions
      const nominalChaserPositions =
        this.maneuverReport?.targetReactionManeuverReport
          ?.relativeDistanceResults?.otherObjectPositions

      if (this.availableChaserReaction && this.displayChaserReaction === true) {
        return maneuveringChaserPositions ? maneuveringChaserPositions : []
      } else {
        return nominalChaserPositions ? nominalChaserPositions : []
      }
    },

    propagationDates() {
      const dates =
        this.maneuverReport?.evasiveTargetToNominalRelativeDistanceResults
          ?.propagationDates
      return dates ? dates : []
    },

    chaserToNominalTargetDistances() {
      const dates =
        this.maneuverReport?.evasiveTargetToNominalRelativeDistanceResults
          ?.propagationDates
      return dates ? dates : []
    },

    chaserToManeuveringTargetDistances() {
      const dates =
        this.maneuverReport?.evasiveTargetToNominalRelativeDistanceResults
          ?.propagationDates
      return dates ? dates : []
    },

    nominalChaserToManeuveringTargetDistances() {
      const dates =
        this.maneuverReport?.evasiveTargetToNominalRelativeDistanceResults
          ?.propagationDates
      return dates ? dates : []
    },
    targetManeuverDates() {
      return this.maneuverReport.targetReactionManeuverReport.targetImpulseManeuverReports.map(
        (report) => report.dateOfManeuver
      )
    },
    chaserManeuverDates() {
      if (
        this.computeChaserReaction === true &&
        this.availableChaserReaction === true &&
        this.displayChaserReaction === true
      ) {
        return this.maneuverReport.chaserReactionManeuverReport.chaserImpulseManeuverReports.map(
          (report) => report.dateOfManeuver
        )
      } else {
        return []
      }
    }
  },

  methods: {
    updateSatelliteSelector(newIds) {
      this.satelliteWithSphereIds = []
      this.satelliteIds = newIds
    }
  }
}
</script>

<style scoped>
.cardheader {
  background: #3be287;
}
</style>
